import React, {
    useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { useParams } from 'react-router-dom';
import UserContext from 'components/context/UserContext';

// Material
import { makeStyles } from '@material-ui/core/styles';

// Components and Others
import BackButton from 'components/widgets/BackButton';
import Header from 'components/widgets/Header';
import DealStyles from 'styles/modules/DealStyles';
import Container from 'components/widgets/Container';
import CustomerTabs from 'components/modules/customer/read/CustomerTabs';
import MergeBanner from 'components/modules/customer/merge/MergeBanner';
import MergedRecordNotice from 'components/modules/customer/merge/MergedRecordNotice';
import CustomerService from 'services/modules/CustomerService';
import ModalUtils from 'utils/ModalUtils';
import Loading from 'components/widgets/Loading';
import KeyStore from 'utils/KeyStore';
import Permission from 'utils/enum/Permissions';

const useStyles = makeStyles((theme) => {
    const base = DealStyles.details(theme);
    return {
        ...base,
        boxContainer: {
            ...base.boxContainer,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden',
        },
        tabsWrapper: {
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
        },
    };
});

const CustomerDetail = () => {
    const classes = useStyles();
    const { customerCode } = useParams();
    const customerService = useMemo(() => new CustomerService(), []);

    const { userInformation = {} } = useContext(UserContext);
    const isCustomStockEnabled = (userInformation ?? {}).customStockEnabled ?? false;

    const canMerge = KeyStore.isAllowed(Permission.CUSTOMER_MERGE);

    const [loading, setLoading] = useState(true);
    const [customer, setCustomer] = useState(null);
    const [duplicates, setDuplicates] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);

    const loadCustomer = useCallback(() => {
        if (!customerCode) return () => {};
        let active = true;
        setLoading(true);
        setDuplicates(null);

        customerService.getMergedCustomerInfoLight(customerCode)
            .then((response) => {
                if (!active) return;
                const { data, graphQLErrors } = response;
                if (graphQLErrors) {
                    ModalUtils.errorMessage(graphQLErrors);
                    setCustomer(null);
                    setDuplicates([]);
                    return;
                }
                const customerData = data?.getMergedCustomerInfo || null;
                setCustomer(customerData);

                if (customerData && customerData.status !== 'Merged' && canMerge) {
                    customerService.getCustomerDuplicatesForMerge(customerCode)
                        .then((dupResponse) => {
                            if (!active) return;
                            const { data: dupData } = dupResponse;
                            setDuplicates(dupData?.getCustomerDuplicatesForMerge || []);
                        })
                        .catch(() => {
                            if (!active) return;
                            setDuplicates([]);
                        });
                } else {
                    setDuplicates([]);
                }
            })
            .catch((error) => {
                if (!active) return;
                ModalUtils.errorMessage(error?.graphQLErrors || [error]);
                setCustomer(null);
                setDuplicates([]);
            })
            .finally(() => {
                if (active) setLoading(false);
            });

        return () => {
            active = false;
        };
    }, [customerCode, customerService, canMerge]);

    useEffect(() => {
        const cancel = loadCustomer();
        return cancel;
    }, [loadCustomer, reloadKey]);

    const onMergeCompleted = () => {
        setReloadKey((prev) => prev + 1);
    };

    const isMerged = customer?.status === 'Merged';

    return (
        <Container className="floatBox">
            <Header divider>
                <div>
                    <BackButton route="/customers" />
                </div>
            </Header>
            <Container className={classes.boxContainer}>
                {loading && <Loading />}
                {!loading && isMerged && (
                    <MergedRecordNotice
                        customer={customer}
                        masterCustomerCode={customer.mergedIntoCustomerCode}
                    />
                )}
                {!loading && customer && !isMerged && (
                    <>
                        <MergeBanner
                            customerCode={customerCode}
                            duplicates={duplicates || []}
                            onMergeCompleted={onMergeCompleted}
                        />
                        <div className={classes.tabsWrapper}>
                            <CustomerTabs
                                key={reloadKey}
                                customerCode={customerCode}
                                isCustomStockEnabled={isCustomStockEnabled}
                                duplicates={duplicates}
                                onMergeCompleted={onMergeCompleted}
                            />
                        </div>
                    </>
                )}
            </Container>
        </Container>
    );
};

export default CustomerDetail;
